import moment from 'moment';
import { CircularProgress } from '@mui/material';
import { useMemo } from 'react';
import TableGrid from '../../../../../components/table-grid';
import Utils from '../../../../../utils/immersive_utils';
import { parseUtcTimestamp } from '../../../../../providers/portfolio-performance/transactions/parse-utc-timestamp';
import {
  classifyInstrument,
  parseOptionExpiry,
  parseOptionStrike,
} from '../positions-card/classify-position';
import { normalizeExecRole } from './exec-role';
import './styles.scss';

// 'live' = Portfolio Performance (real venue fills, backend `price`/`position`);
// 'backtest' = the backtester's Transaction Log, which carries the engine's
// authoritative Transaction struct (spot/future/volatility/premium/fee_rate)
// instead of a scalar price, and no position book. The variant switches the
// column set and how a handful of cells are derived; every other cell is shared.
type TransactionsTableVariant = 'live' | 'backtest';

type PortfolioPerformanceTransactionsTableProps = {
  data: any[];
  loading: boolean;
  variant?: TransactionsTableVariant;
  // Backtest-only: the backtester emits no position book, so compute a running
  // signed position per instrument by accumulating trade amounts down the
  // (time-ordered) log. Off by default so live Portfolio Performance keeps its
  // real backend `position` field untouched; even when on, it only fills rows
  // where the backend position is absent.
  runningPosition?: boolean;
};

interface RawTx {
  [key: string]: any;
}

type GridRow = {
  instrument: string;
  // Display-only instrument name. Same as `instrument` for live rows; for
  // backtest rows the theoretical `option-<strike>` name has its long
  // delta-derived strike rounded for legibility (see roundSyntheticOptionName).
  displayInstrument: string;
  strike: string;
  expiry: string;
  type: string;
  side: string;
  sideColor: string;
  role: string;
  roleColor: string;
  amount: string;
  premiumUsd: string;
  executionPrice: string;
  vol: string;
  position: string;
  isNegativePosition: boolean;
  feeCharged: string;
  feeColor: string;
  feeRate: string;
  date: string;
};

// Rounds the long delta-derived strike in a theoretical option name for
// display, e.g. "option-45160.66729284" -> "option-45161". Integer for
// price-scale strikes, a couple of decimals for small values. Exchange-format
// names ("BTC-25DEC26-80000-C") don't match the shape and pass through
// untouched. Display-only — the raw name is kept for any downstream parsing.
const roundSyntheticOptionName = (name: string): string => {
  const match = /^(option-)(-?\d+(?:\.\d+)?)(-[CP])?$/i.exec(name);
  if (!match) return name;
  const value = Number(match[2]);
  if (!Number.isFinite(value)) return name;
  const rounded = Math.abs(value) >= 100 ? Math.round(value) : Math.round(value * 100) / 100;
  return `${match[1]}${rounded}${match[3] ?? ''}`;
};

// Cell renderers, shared by both column sets so live and backtest render
// identical chrome. Module-scoped so the column arrays reference one instance.
const InstrumentCell = ({ value, data }: { value: string; data: GridRow }) => (
  <span className="transactions-table-instrument">
    <span className="transactions-table-instrument-name">{value}</span>
    {(data.strike || data.expiry) && (
      <span className="transactions-table-instrument-sub">
        {[data.strike, data.expiry].filter(Boolean).join(' · ')}
      </span>
    )}
  </span>
);

// Backtest instrument cell: no ellipsis so the full (rounded) theoretical
// option name renders, wrapping within its widened column if ever needed.
const BacktestInstrumentCell = ({ value, data }: { value: string; data: GridRow }) => (
  <span className="transactions-table-instrument transactions-table-instrument--full">
    <span className="transactions-table-instrument-name">{value}</span>
    {(data.strike || data.expiry) && (
      <span className="transactions-table-instrument-sub">
        {[data.strike, data.expiry].filter(Boolean).join(' · ')}
      </span>
    )}
  </span>
);

const SideCell = ({ value, data }: { value: string; data: GridRow }) => (
  <span className="transactions-table-side">
    <span style={{ color: data.sideColor }}>{value}</span>
    {data.role && <span style={{ color: data.roleColor }}> {data.role}</span>}
  </span>
);

const PositionCell = ({ value, data }: { value: string; data: GridRow }) => (
  <span className={data.isNegativePosition ? 'transactions-table-position-negative' : ''}>
    {value}
  </span>
);

const FeeColorCell = ({ value, data }: { value: string; data: GridRow }) => (
  <span style={{ color: data.feeColor }}>{value}</span>
);

function getFirstValid(items: Array<string | null | undefined>): string {
  for (const item of items) {
    if (item != null && item !== '') return item;
  }
  return '-';
}

// Deribit reports PERPETUAL, OKX reports SWAP for the same conceptual product.
const PERP_TYPES = new Set(['PERPETUAL', 'SWAP']);

// Same instrument-name convention as positions-card/classify-position.ts —
// options always end "-<strike>-C" or "-<strike>-P".
const isOptionInstrument = (instrument: string): boolean => /-(C|P)$/.test(instrument);

// Deribit perpetuals are USD-notional inverse contracts, so `amount` (USD)
// has to be converted to underlying units via price to get a fee rate that
// matches Deribit's own UI (verified: -2.828e-05 / (17770/62833.5) =
// -0.0001 = -0.01%, matching the Fee Rate shown for that fill). Options
// contracts are already sized in underlying units, so `amount` is used as-is.
const notionalInUnderlying = (tx: RawTx): number => {
  const amount = parseFloat(getFirstValid([tx.amount, tx.qty, tx.volume]));
  if (!Number.isFinite(amount)) return NaN;
  if (PERP_TYPES.has(tx.instrument_type)) {
    const price = parseFloat(tx.price);
    return Number.isFinite(price) && price !== 0 ? amount / price : NaN;
  }
  return amount;
};

// Same normalisation as getFee() in transactions-metrics.tsx: Deribit's
// `commission` and OKX's `fee` use OPPOSITE raw sign conventions (verified
// live against `user_role`/`execType` on each venue), so `fee` gets negated
// here to match `commission`'s "positive = cost, negative = rebate" shape.
const getRawFee = (tx: RawTx): number => {
  if (tx.commission !== undefined) {
    const parsed = parseFloat(tx.commission);
    return Number.isFinite(parsed) ? parsed : NaN;
  }
  if (tx.fee !== undefined) {
    const parsed = parseFloat(tx.fee);
    return Number.isFinite(parsed) ? -parsed : NaN;
  }
  return NaN;
};

export const PortfolioPerformanceTransactionsTable = ({
  data,
  loading = false,
  variant = 'live',
  runningPosition = false,
}: PortfolioPerformanceTransactionsTableProps) => {
  const isBacktest = variant === 'backtest';

  const formatValueUSD = (priceCurrency: string, value: string, decimals = 2) => {
    return priceCurrency === 'USD'
      ? Utils.valueToCurrency(parseFloat(value), decimals)
      : Utils.formatNumber(parseFloat(value), decimals);
  };

  const mapTransactionToGridRow = (
    tx: RawTx,
    runningPositionValue: number | null = null,
  ): GridRow => {
    const instrument = getFirstValid([tx.instrument_name, tx.instId, tx.instrument]);
    // Backtest only: round the long theoretical strike for display; live names
    // pass through unchanged.
    const displayInstrument = isBacktest ? roundSyntheticOptionName(instrument) : instrument;
    const isOption = isOptionInstrument(instrument);
    // Strike + expiry parsed from the instrument name (Deribit + OKX
    // conventions) — options only; blank for perps/futures. Surfaced as a
    // compact sub-line under the instrument rather than their own columns.
    const strike = isOption ? (parseOptionStrike(instrument) ?? '') : '';
    const expiry = isOption ? (parseOptionExpiry(instrument) ?? '') : '';
    const type = getFirstValid([tx.event_type, tx.type]);
    const sideAction = getFirstValid([tx.side, tx.action]);
    const sideColor =
      typeof sideAction === 'string' && sideAction.toLowerCase().includes('sell')
        ? '#D82C0D'
        : typeof sideAction === 'string' &&
            (sideAction.toLowerCase().includes('buy') || sideAction.toLowerCase().includes('long'))
          ? '#36C88A'
          : '';
    // Folded into the Side column (e.g. "close sell (maker)") rather than
    // its own column, to save horizontal space in an already-wide table —
    // but coloured independently of the side (green maker / red taker),
    // since a maker fill is generally the favourable side regardless of
    // buy/sell direction. Normalised across venues (Deribit `user_role`,
    // OKX `execType`) so OKX rows get the tag too — see normalizeExecRole.
    const execRole = normalizeExecRole(tx.user_role ?? tx.execType);
    const role = execRole ? `(${execRole})` : '';
    const roleColor = execRole === 'maker' ? '#36C88A' : execRole === 'taker' ? '#D82C0D' : '';
    const rawAmount = getFirstValid([tx.amount, tx.qty, tx.volume]);
    const amountNum = parseFloat(rawAmount);
    const amount = !isNaN(amountNum) ? formatValueUSD(tx.price_currency, rawAmount) : '-';

    const rawFee = getRawFee(tx);

    // Execution price / rate, Vol, Premium and Fee Rate are derived differently
    // per variant. The `live` branch is the original Portfolio Performance
    // logic, preserved exactly. The `backtest` branch reads the engine's
    // Transaction struct fields (no scalar price exists there).
    let executionPrice = '-';
    let vol = '';
    let premiumUsd = '-';
    let feeRate = '-';

    if (isBacktest) {
      // Exec price / rate: perps & dated futures -> `future` (the execution
      // rate, authoritative per William); spot trades -> `spot`; options have
      // no scalar price, so the Vol column carries `volatility` instead.
      const kind = classifyInstrument(instrument);
      if (kind === 'futures') {
        const future = parseFloat(tx.future);
        executionPrice = Number.isFinite(future)
          ? formatValueUSD(tx.price_currency, tx.future)
          : '-';
      } else if (kind === 'token') {
        const spot = parseFloat(tx.spot);
        executionPrice = Number.isFinite(spot) ? formatValueUSD(tx.price_currency, tx.spot) : '-';
      }
      if (isOption) {
        const volatility = parseFloat(tx.volatility);
        // Engine volatility is a decimal (e.g. 0.65) — shown as a percentage.
        vol = Number.isFinite(volatility) ? `${(volatility * 100).toFixed(2)}%` : '-';
      }
      const premiumValue = parseFloat(tx.premium);
      premiumUsd = Number.isFinite(premiumValue)
        ? formatValueUSD(tx.price_currency, tx.premium)
        : '-';
      // Fee Rate comes straight from the engine's `fee_rate` (for options it's
      // the config options_fee, e.g. 0.00025). Shown in bps to match how the
      // fee was entered (2.5 bps), not a tiny 0.025%.
      const feeRateValue = parseFloat(tx.fee_rate);
      feeRate = Number.isFinite(feeRateValue) ? `${(feeRateValue * 10000).toFixed(1)} bps` : '-';
    } else {
      const rawExecutionPrice = getFirstValid([tx.price, tx.px, tx.fillPxUsd]);
      const priceNum = parseFloat(rawExecutionPrice);
      // 2dp hides the price entirely for options, which typically trade at a
      // few hundredths or less (e.g. 0.0173) — perps/futures execution prices
      // are large enough that 2dp is still meaningful.
      const executionPriceDecimals = isOption ? 4 : 2;
      executionPrice = !isNaN(priceNum)
        ? formatValueUSD(tx.price_currency, rawExecutionPrice, executionPriceDecimals)
        : '-';
      // Premium (USD): the trade's cash value. Only meaningful for options
      // (contracts x premium) — perps carry a USD notional, not a premium, so
      // they read '-' here rather than a redundant/misleading number.
      const premium =
        isOption && !isNaN(amountNum) && !isNaN(priceNum) ? amountNum * priceNum : NaN;
      premiumUsd = Number.isFinite(premium)
        ? formatValueUSD(tx.price_currency, String(Math.abs(premium)))
        : '-';
      // Fee rate as a percentage of notional. For options the old
      // fee / underlying-amount ratio is nonsensical (fee is a fraction of the
      // *premium*, not of the contract's underlying size — it produced values
      // like -1065%), so options are rated against premium (|amount x price|).
      // Perps/futures keep the underlying-notional basis.
      if (Number.isFinite(rawFee)) {
        if (isOption) {
          if (Number.isFinite(premium) && premium !== 0) {
            feeRate = `${((rawFee / Math.abs(premium)) * 100).toFixed(2)}%`;
          }
        } else {
          const notional = notionalInUnderlying(tx);
          if (Number.isFinite(notional) && notional !== 0) {
            feeRate = `${((rawFee / notional) * 100).toFixed(2)}%`;
          }
        }
      }
    }
    // Running-position mode (backtest) supplies a computed signed position;
    // otherwise use the backend `position` field (live Portfolio Performance).
    const rawPosition = getFirstValid([tx.position, tx.posBalChg, tx.positionChange]);
    const parsedPosition =
      runningPositionValue != null ? runningPositionValue : parseFloat(rawPosition);
    const isNegativePosition = Number.isFinite(parsedPosition) && parsedPosition < 0;
    const formattedPosition = Number.isFinite(parsedPosition)
      ? formatValueUSD(tx.price_currency, String(parsedPosition))
      : '-';
    const position =
      formattedPosition !== '-' && isNegativePosition
        ? `(${formattedPosition.replace('-', '')})`
        : formattedPosition;
    const rawTimestamp = getFirstValid([tx.timestamp, tx.time, tx.date]);
    const parsedTimestamp = rawTimestamp !== '-' ? parseUtcTimestamp(rawTimestamp) : null;
    const date = parsedTimestamp
      ? `${moment.utc(parsedTimestamp).format('DD MMM YYYY - HH:mm:ss')} UTC`
      : '-';

    const feeCharged = Number.isFinite(rawFee)
      ? `${Utils.formatNumber(rawFee, 6)} ${tx.currency || tx.ccy || ''}`.trim()
      : '-';
    const feeColor = Number.isFinite(rawFee) ? (rawFee < 0 ? '#36C88A' : '#D82C0D') : '';

    return {
      instrument,
      displayInstrument,
      strike,
      expiry,
      type,
      side: sideAction,
      sideColor,
      role,
      roleColor,
      amount,
      premiumUsd,
      executionPrice,
      vol,
      position,
      isNegativePosition,
      feeCharged,
      feeColor,
      feeRate,
      date,
    };
  };

  const gridData = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }
    // Running position (backtest only): accumulate signed trade amounts per
    // instrument down the already time-ordered log, and only for rows where the
    // backend omits a real `position`. Off => live PP path, backend position used.
    const runningByInstrument: Record<string, number> = {};
    return data.map((tx) => {
      let runningPositionValue: number | null = null;
      if (runningPosition) {
        const backendPosition = getFirstValid([tx.position, tx.posBalChg, tx.positionChange]);
        const amountNum = parseFloat(getFirstValid([tx.amount, tx.qty, tx.volume]));
        if (backendPosition === '-' && Number.isFinite(amountNum)) {
          const instrument = getFirstValid([tx.instrument_name, tx.instId, tx.instrument]);
          runningByInstrument[instrument] = (runningByInstrument[instrument] ?? 0) + amountNum;
          runningPositionValue = runningByInstrument[instrument];
        }
      }
      return mapTransactionToGridRow(tx, runningPositionValue);
    });
  }, [data, runningPosition]);

  const columns = useMemo(() => {
    if (isBacktest) {
      // Backtest column set: reads the engine's Transaction struct. No Type/Side
      // (the action is the identity, direction is in the signed amount), adds a
      // Vol column for options, and Position is the client-side running total.
      // Kept deliberately lean — William dislikes clutter.
      return [
        {
          field: 'date',
          headerName: 'Date',
          sortable: false,
          flex: 1.6,
          minWidth: 190,
        },
        {
          field: 'displayInstrument',
          headerName: 'Instrument',
          sortable: false,
          // Wide enough that theoretical option names (rounded, e.g.
          // "option-45161") render full-size without truncation.
          flex: 3,
          minWidth: 280,
          cellRenderer: BacktestInstrumentCell,
        },
        {
          field: 'type',
          headerName: 'Action',
          sortable: false,
          flex: 0.9,
          minWidth: 100,
        },
        {
          field: 'amount',
          headerName: 'Amount',
          sortable: false,
          flex: 0.8,
          minWidth: 95,
          align: 'right' as const,
        },
        {
          field: 'executionPrice',
          headerName: 'Exec Price / Rate',
          sortable: false,
          flex: 1.1,
          minWidth: 130,
          align: 'right' as const,
        },
        {
          field: 'vol',
          headerName: 'Vol',
          sortable: false,
          flex: 0.6,
          minWidth: 70,
          align: 'right' as const,
        },
        {
          field: 'premiumUsd',
          headerName: 'Premium',
          sortable: false,
          flex: 1,
          minWidth: 120,
          align: 'right' as const,
        },
        {
          field: 'feeCharged',
          headerName: 'Fee',
          sortable: false,
          flex: 0.8,
          minWidth: 100,
          align: 'right' as const,
          cellRenderer: FeeColorCell,
        },
        {
          field: 'feeRate',
          headerName: 'Fee Rate',
          sortable: false,
          flex: 0.8,
          minWidth: 100,
          align: 'right' as const,
          cellRenderer: FeeColorCell,
        },
        {
          field: 'position',
          headerName: 'Position',
          sortable: false,
          flex: 1,
          minWidth: 120,
          align: 'right' as const,
          cellRenderer: PositionCell,
        },
      ];
    }

    // Live Portfolio Performance column set — unchanged.
    return [
      {
        field: 'instrument',
        headerName: 'Instrument',
        sortable: false,
        flex: 2,
        minWidth: 170,
        cellRenderer: InstrumentCell,
      },
      {
        field: 'type',
        headerName: 'Type',
        sortable: false,
        flex: 0.7,
        minWidth: 80,
      },
      {
        field: 'side',
        headerName: 'Side',
        sortable: false,
        flex: 1.1,
        minWidth: 130,
        cellRenderer: SideCell,
      },
      {
        field: 'amount',
        headerName: 'Amount',
        sortable: false,
        flex: 0.9,
        minWidth: 110,
        align: 'right' as const,
      },
      {
        field: 'premiumUsd',
        headerName: 'Premium (USD)',
        sortable: false,
        flex: 1.1,
        minWidth: 130,
        align: 'right' as const,
      },
      {
        field: 'executionPrice',
        headerName: 'Execution Price',
        sortable: false,
        flex: 1.2,
        minWidth: 140,
        align: 'right' as const,
      },
      {
        field: 'position',
        headerName: 'Position',
        sortable: false,
        flex: 1,
        minWidth: 120,
        align: 'right' as const,
        cellRenderer: PositionCell,
      },
      {
        field: 'feeCharged',
        headerName: 'Fee Charged',
        sortable: false,
        flex: 1.2,
        minWidth: 140,
        align: 'right' as const,
        cellRenderer: FeeColorCell,
      },
      {
        field: 'feeRate',
        headerName: 'Fee Rate',
        sortable: false,
        flex: 0.8,
        minWidth: 100,
        align: 'right' as const,
        cellRenderer: FeeColorCell,
      },
      {
        field: 'date',
        headerName: 'DateTime',
        sortable: false,
        flex: 1.8,
        minWidth: 210,
      },
    ];
  }, [isBacktest]);

  if (loading) {
    return (
      <div className="loading-transactions-table">
        <CircularProgress size={28} />
      </div>
    );
  }

  return (
    <div className="portfolio-performance-transactions-grid-wrapper">
      <TableGrid
        columns={columns}
        rowData={gridData}
        className="portfolio-performance-transactions-grid"
        fitWidth
        noWatermark
        // Matches the Positions tab's compact row density (12px/28px vs the
        // 14px/32px default elsewhere) — this table only, via the optional
        // per-instance overrides on TableGrid.
        fontSize="12px"
        rowHeight={28}
      />
    </div>
  );
};
